import type { FC, ReactNode } from 'react'

import { CircleCheck, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

type ToastTone = 'success' | 'error'

interface ToastMessage {
    tone: ToastTone
    title: string
    description?: string
}

interface ToastItem extends ToastMessage {
    id: number
}

interface ToastContextValue {
    notify: (message: ToastMessage) => void
}

interface ToastProviderProps {
    children: ReactNode
}

const ToastContext = createContext<ToastContextValue>({ notify: () => undefined })

export const useToast = (): ToastContextValue => useContext(ToastContext)

export const ToastProvider: FC<ToastProviderProps> = ({ children }): ReactNode => {
    const [toasts, setToasts] = useState<ToastItem[]>([])
    const nextId = useRef(0)
    const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

    const dismiss = useCallback((id: number) => {
        setToasts(current => current.filter(toast => toast.id !== id))

        const timer = timers.current.get(id)

        if (timer) clearTimeout(timer)

        timers.current.delete(id)
    }, [])

    const notify = useCallback((message: ToastMessage) => {
        nextId.current += 1

        const id = nextId.current

        setToasts(current => [...current.slice(-2), { ...message, id }])
        timers.current.set(id, setTimeout(() => dismiss(id), 6000))
    }, [dismiss])

    useEffect(() => {
        const active = timers.current

        return () => active.forEach(timer => clearTimeout(timer))
    }, [])

    const value = useMemo(() => ({ notify }), [notify])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div
                aria-live='polite'
                className='pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6'
            >
                {toasts.map(toast => {
                    const Icon = toast.tone === 'success' ? CircleCheck : TriangleAlert

                    return (
                        <div
                            key={toast.id}
                            role='status'
                            className='pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200'
                        >
                            <Icon
                                aria-hidden='true'
                                className={cn('mt-0.5 size-5 shrink-0', toast.tone === 'success' ? 'text-success' : 'text-danger')}
                            />

                            <div className='min-w-0 flex-1'>
                                <p className='text-sm font-semibold text-foreground'>
                                    {toast.title}
                                </p>

                                {toast.description && (
                                    <p className='mt-0.5 text-sm text-muted-foreground'>
                                        {toast.description}
                                    </p>
                                )}
                            </div>

                            <button
                                type='button'
                                onClick={() => dismiss(toast.id)}
                                className='-m-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <X aria-hidden='true' className='size-4' />
                                <span className='sr-only'>Dismiss</span>
                            </button>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}
